import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

type Entry={id:string;active:boolean;owns:boolean;close:()=>void};
let sequence=0;
const stack:Entry[]=[];
const known=new Map<string,Entry>();
let rewindPending:Promise<void>|null=null;
let finishRewind:(()=>void)|null=null;
let listening=false;
const focusable='button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function rewind(){
  if(rewindPending)return;
  rewindPending=new Promise(resolve=>{finishRewind=resolve});
  history.back();
}
function listen(){
  if(listening)return;listening=true;
  window.addEventListener('popstate',event=>{
    const current=known.get(history.state?.vgenDialog);
    // A successful action may unmount several nested dialogs together.
    // Consume only those owned history entries, never a real page entry.
    if(rewindPending){
      event.stopImmediatePropagation();
      if(current&&!current.active){history.back();return}
      const finish=finishRewind;rewindPending=null;finishRewind=null;finish?.();return;
    }
    const top=stack.at(-1);
    if(top?.owns&&history.state?.vgenDialog!==top.id){
      event.stopImmediatePropagation();top.close();
    }
  },true);
}
function register(entry:Entry){
  listen();stack.push(entry);known.set(entry.id,entry);
  const push=()=>{
    if(!entry.active)return;
    const state=history.state&&typeof history.state==='object'?history.state:{};
    // Existing studio/video surfaces own their first overlay entry.
    // Nested dialogs always own a separate entry so Back closes one level.
    if(stack.indexOf(entry)===0&&(state.vgenStudioOverlay||state.vgenVideo))return;
    history.pushState({...state,vgenDialog:entry.id},'');entry.owns=true;
  };
  if(rewindPending)void rewindPending.then(push);else push();
  return ()=>{
    entry.active=false;const index=stack.indexOf(entry);if(index>=0)stack.splice(index,1);
    if(entry.owns&&history.state?.vgenDialog===entry.id)rewind();
  };
}

export function AppDialog({title,onClose,wide=false,children}:{title:string;onClose:()=>void;wide?:boolean;children:ReactNode}){
  const dialogRef=useRef<HTMLElement>(null);
  const closeRef=useRef(onClose);closeRef.current=onClose;
  const entryRef=useRef<Entry|null>(null);
  if(!entryRef.current)entryRef.current={id:`dialog-${++sequence}`,active:true,owns:false,close:()=>closeRef.current()};
  const close=()=>{if(stack.at(-1)===entryRef.current)closeRef.current()};
  useEffect(()=>{
    const entry=entryRef.current!;entry.active=true;
    const previous=document.activeElement instanceof HTMLElement?document.activeElement:null;
    const unregister=register(entry);
    const pop=()=>{if(!entry.owns&&stack.at(-1)===entry)closeRef.current()};
    const key=(event:KeyboardEvent)=>{
      if(stack.at(-1)!==entry)return;
      if(event.key==='Escape'){event.preventDefault();event.stopImmediatePropagation();closeRef.current();return}
      if(event.key!=='Tab')return;
      const nodes=[...(dialogRef.current?.querySelectorAll<HTMLElement>(focusable)??[])].filter(node=>!node.closest('[hidden],[inert]'));
      const first=nodes[0],last=nodes.at(-1);
      if(!first){event.preventDefault();dialogRef.current?.focus()}
      else if(event.shiftKey&&(document.activeElement===first||document.activeElement===dialogRef.current)){event.preventDefault();last?.focus()}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus()}
    };
    window.addEventListener('popstate',pop);document.addEventListener('keydown',key);
    queueMicrotask(()=>{if(entry.active)(dialogRef.current?.querySelector<HTMLElement>(focusable)??dialogRef.current)?.focus()});
    return()=>{window.removeEventListener('popstate',pop);document.removeEventListener('keydown',key);unregister();if(previous?.isConnected)previous.focus()};
  },[]);
  return <div className="modal-backdrop" onMouseDown={event=>{if(event.target===event.currentTarget)close()}}>
    <section ref={dialogRef} tabIndex={-1} className={`modal ${wide?'wide':''}`} role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-head"><h2>{title}</h2><button type="button" className="icon-button" aria-label="Закрыть" onClick={close}><X/></button></div>
      {children}
    </section>
  </div>;
}
